/**
 * SoundManager — Module-level Web Audio API singleton.
 * Provides cinematic UI sounds without any external dependencies.
 * Volume is kept at ~0.4 so sounds are satisfying but never overpower Luna's voice.
 */

let _ctx: AudioContext | null = null;

const getCtx = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;
  if (!_ctx || _ctx.state === 'closed') {
    _ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (_ctx.state === 'suspended') {
    _ctx.resume();
  }
  return _ctx;
};

type SoundType = 'ui_open' | 'ui_click' | 'cha_ching' | 'product_select' | 'sale_complete';

const MASTER_VOL = 0.4;

const playUIOpen = (ctx: AudioContext) => {
  const now = ctx.currentTime;

  // Layer 1 — Low rumble swell (power-up foundation)
  const rumble = ctx.createOscillator();
  const rumbleGain = ctx.createGain();
  rumble.type = 'sine';
  rumble.frequency.setValueAtTime(60, now);
  rumble.frequency.exponentialRampToValueAtTime(180, now + 0.35);
  rumbleGain.gain.setValueAtTime(0, now);
  rumbleGain.gain.linearRampToValueAtTime(MASTER_VOL * 0.18, now + 0.05);
  rumbleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
  rumble.connect(rumbleGain);
  rumbleGain.connect(ctx.destination);
  rumble.start(now);
  rumble.stop(now + 0.5);

  // Layer 2 — Mid shimmer sweep (the 'swoosh')
  const sweep = ctx.createOscillator();
  const sweepGain = ctx.createGain();
  sweep.type = 'sine';
  sweep.frequency.setValueAtTime(440, now + 0.05);
  sweep.frequency.exponentialRampToValueAtTime(1760, now + 0.35);
  sweepGain.gain.setValueAtTime(0, now + 0.05);
  sweepGain.gain.linearRampToValueAtTime(MASTER_VOL * 0.22, now + 0.12);
  sweepGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
  sweep.connect(sweepGain);
  sweepGain.connect(ctx.destination);
  sweep.start(now + 0.05);
  sweep.stop(now + 0.5);

  // Layer 3 — High crystal sparkle (arrival confirmation)
  const sparkle = ctx.createOscillator();
  const sparkleGain = ctx.createGain();
  sparkle.type = 'sine';
  sparkle.frequency.setValueAtTime(2200, now + 0.28);
  sparkle.frequency.exponentialRampToValueAtTime(3300, now + 0.45);
  sparkleGain.gain.setValueAtTime(0, now + 0.28);
  sparkleGain.gain.linearRampToValueAtTime(MASTER_VOL * 0.12, now + 0.32);
  sparkleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
  sparkle.connect(sparkleGain);
  sparkleGain.connect(ctx.destination);
  sparkle.start(now + 0.28);
  sparkle.stop(now + 0.55);
};

const playUIClick = (ctx: AudioContext) => {
  const now = ctx.currentTime;

  // Sharp transient body — the 'tick'
  const tick = ctx.createOscillator();
  const tickGain = ctx.createGain();
  tick.type = 'square';
  tick.frequency.setValueAtTime(1800, now);
  tick.frequency.exponentialRampToValueAtTime(600, now + 0.04);
  tickGain.gain.setValueAtTime(MASTER_VOL * 0.35, now);
  tickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
  tick.connect(tickGain);
  tickGain.connect(ctx.destination);
  tick.start(now);
  tick.stop(now + 0.08);

  // Soft sub-click for body/warmth
  const sub = ctx.createOscillator();
  const subGain = ctx.createGain();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(220, now);
  sub.frequency.exponentialRampToValueAtTime(110, now + 0.05);
  subGain.gain.setValueAtTime(MASTER_VOL * 0.15, now);
  subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.07);
  sub.connect(subGain);
  subGain.connect(ctx.destination);
  sub.start(now);
  sub.stop(now + 0.08);
};

const playProductSelect = (ctx: AudioContext) => {
  const now = ctx.currentTime;

  // Crystal bell body — soft high sine ping
  const bell = ctx.createOscillator();
  const bellGain = ctx.createGain();
  bell.type = 'sine';
  bell.frequency.setValueAtTime(2400, now);
  bell.frequency.exponentialRampToValueAtTime(3600, now + 0.06);
  bellGain.gain.setValueAtTime(0, now);
  bellGain.gain.linearRampToValueAtTime(MASTER_VOL * 0.14, now + 0.012);
  bellGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  bell.connect(bellGain);
  bellGain.connect(ctx.destination);
  bell.start(now);
  bell.stop(now + 0.18);

  // Harmonic shimmer overtone
  const shimmer = ctx.createOscillator();
  const shimmerGain = ctx.createGain();
  shimmer.type = 'sine';
  shimmer.frequency.setValueAtTime(4800, now + 0.02);
  shimmer.frequency.exponentialRampToValueAtTime(6000, now + 0.1);
  shimmerGain.gain.setValueAtTime(0, now + 0.02);
  shimmerGain.gain.linearRampToValueAtTime(MASTER_VOL * 0.06, now + 0.04);
  shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
  shimmer.connect(shimmerGain);
  shimmerGain.connect(ctx.destination);
  shimmer.start(now + 0.02);
  shimmer.stop(now + 0.18);
};

const playChaChingSound = (ctx: AudioContext) => {
  const now = ctx.currentTime;

  // Coin hit — bright metallic ping
  [0, 0.07, 0.13].forEach((offset, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(1800 + i * 220, now + offset);
    osc.frequency.exponentialRampToValueAtTime(900 + i * 100, now + offset + 0.18);
    gain.gain.setValueAtTime(MASTER_VOL * 0.45, now + offset);
    gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + offset);
    osc.stop(now + offset + 0.3);
  });

  // Register 'ka' thunk
  const thunk = ctx.createOscillator();
  const thunkGain = ctx.createGain();
  thunk.type = 'square';
  thunk.frequency.setValueAtTime(280, now + 0.18);
  thunk.frequency.exponentialRampToValueAtTime(80, now + 0.32);
  thunkGain.gain.setValueAtTime(MASTER_VOL * 0.3, now + 0.18);
  thunkGain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);
  thunk.connect(thunkGain);
  thunkGain.connect(ctx.destination);
  thunk.start(now + 0.18);
  thunk.stop(now + 0.4);

  // Triumphant high sparkle tail
  const sparkle = ctx.createOscillator();
  const sparkleGain = ctx.createGain();
  sparkle.type = 'sine';
  sparkle.frequency.setValueAtTime(3200, now + 0.22);
  sparkle.frequency.exponentialRampToValueAtTime(4800, now + 0.42);
  sparkleGain.gain.setValueAtTime(MASTER_VOL * 0.18, now + 0.22);
  sparkleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.48);
  sparkle.connect(sparkleGain);
  sparkleGain.connect(ctx.destination);
  sparkle.start(now + 0.22);
  sparkle.stop(now + 0.5);
};

export const SoundManager = {
  play: (type: SoundType) => {
    const ctx = getCtx();
    if (!ctx) return;
    if (type === 'ui_open') playUIOpen(ctx);
    if (type === 'ui_click') playUIClick(ctx);
    if (type === 'cha_ching' || type === 'sale_complete') playChaChingSound(ctx);
    if (type === 'product_select') playProductSelect(ctx);
  }
};
